import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Stack, Autocomplete, Checkbox, FormControlLabel, FormGroup, Typography,
  CircularProgress, Alert, Chip,
} from '@mui/material';
import { TareoService } from '../services/tareoService';
import { TrabajadorService } from '../../trabajadores/services/trabajadorService';
import { TurnosService } from '../../turnos/services/turnosService';
import type { Trabajador } from '../../trabajadores/types/trabajador';
import type { Turno } from '../../turnos/types/turnos';

const DIAS_SEMANA = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AsignarTurnoDialog({ open, onClose, onSuccess }: Props) {
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [trabajadores, setTrabajadores] = useState<Trabajador[]>([]);
  const [selectedTurno, setSelectedTurno] = useState<Turno | null>(null);
  const [selectedTrabajadores, setSelectedTrabajadores] = useState<Trabajador[]>([]);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [diasSeleccionados, setDiasSeleccionados] = useState<number[]>([1, 2, 3, 4, 5]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadData();
      // Default: current month
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      setFechaDesde(`${y}-${m}-01`);
      const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
      setFechaHasta(`${y}-${m}-${String(lastDay).padStart(2, '0')}`);
    }
  }, [open]);

  const loadData = async () => {
    try {
      const [t, w] = await Promise.all([
        TurnosService.getAll(),
        TrabajadorService.getAll(),
      ]);
      setTurnos(t.filter(x => x.activo));
      setTrabajadores(w.filter(x => x.activo));
    } catch { /* handled */ }
  };

  const toggleDia = (dia: number) => {
    setDiasSeleccionados(prev =>
      prev.includes(dia) ? prev.filter(d => d !== dia) : [...prev, dia].sort()
    );
  };

  const selectLunVie = () => setDiasSeleccionados([1, 2, 3, 4, 5]);
  const selectLunSab = () => setDiasSeleccionados([1, 2, 3, 4, 5, 6]);

  const handleSave = async () => {
    setError(null);
    if (!selectedTurno) { setError('Seleccione un turno'); return; }
    if (selectedTrabajadores.length === 0) { setError('Seleccione al menos un trabajador'); return; }
    if (!fechaDesde || !fechaHasta) { setError('Ingrese las fechas'); return; }
    if (diasSeleccionados.length === 0) { setError('Seleccione al menos un día'); return; }

    setSaving(true);
    try {
      const result = await TareoService.asignarTurnoMasivo({
        idTurno: selectedTurno.idTurno,
        idTrabajadores: selectedTrabajadores.map(t => t.idTrabajador),
        fechaDesde,
        fechaHasta,
        diaSemana: diasSeleccionados.join(','),
      });
      alert(result.message);
      setSelectedTurno(null);
      setSelectedTrabajadores([]);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al asignar turno');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Asignar Turno a Trabajadores</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={turnos}
            getOptionLabel={(o) => `${o.descripcion} (${o.horaInicio?.substring(0, 5)} - ${o.horaFin?.substring(0, 5)})`}
            value={selectedTurno}
            onChange={(_, v) => setSelectedTurno(v)}
            renderInput={(p) => <TextField {...p} label="Turno" size="small" />}
          />

          <Autocomplete
            multiple
            options={trabajadores}
            getOptionLabel={(o) => o.personal?.personaContacto ?? `#${o.idTrabajador}`}
            value={selectedTrabajadores}
            onChange={(_, v) => setSelectedTrabajadores(v)}
            renderInput={(p) => <TextField {...p} label="Trabajadores" size="small" />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...rest } = getTagProps({ index });
                return <Chip key={key} label={option.personal?.personaContacto ?? `#${option.idTrabajador}`} size="small" {...rest} />;
              })
            }
            limitTags={3}
          />
          <Button size="small" variant="text" onClick={() => setSelectedTrabajadores([...trabajadores])}>
            Seleccionar todos ({trabajadores.length})
          </Button>

          <Stack direction="row" spacing={2}>
            <TextField label="Desde" type="date" size="small" fullWidth value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} />
            <TextField label="Hasta" type="date" size="small" fullWidth value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} />
          </Stack>

          <Typography variant="caption" fontWeight="bold">Días de la semana</Typography>
          <FormGroup row>
            {DIAS_SEMANA.map(d => (
              <FormControlLabel key={d.value} control={
                <Checkbox size="small" checked={diasSeleccionados.includes(d.value)}
                  onChange={() => toggleDia(d.value)} />
              } label={d.label} />
            ))}
          </FormGroup>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={selectLunVie}>Lun-Vie</Button>
            <Button size="small" variant="outlined" onClick={selectLunSab}>Lun-Sáb</Button>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Asignar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
